import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, TextField, MenuItem,
  FormControl, InputLabel, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, CircularProgress, Chip, Checkbox, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { toast } from 'react-toastify';
import WhtCertificateService from './WhtCertificateService';

const EMPTY_ROW = { datePaid: null, amount: '' };

const EMPTY_PAYEE = {
  name: '', taxId: '', address: '', payeeType: 'company',
  deductPercentage: 3, incomeDescription: '', incomeBox: 6, numberOfCopy: 2, active: true,
};

const baht = (n) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const WhtCertificatePage = () => {
  const [payees, setPayees] = useState([]);
  const [selectedPayeeId, setSelectedPayeeId] = useState('');
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [creating, setCreating] = useState(false);
  const [showInactivePayees, setShowInactivePayees] = useState(false);

  // Payee add/edit dialog (editingPayeeId null = add mode)
  const [payeeDialogOpen, setPayeeDialogOpen] = useState(false);
  const [payeeForm, setPayeeForm] = useState({ ...EMPTY_PAYEE });
  const [editingPayeeId, setEditingPayeeId] = useState(null);
  const [savingPayee, setSavingPayee] = useState(false);
  const [uploadingIdCard, setUploadingIdCard] = useState(false);
  const [idCardPreview, setIdCardPreview] = useState(null);
  // Add mode only: the card picked before the payee exists, uploaded right after
  // it is created.
  const [pendingIdCard, setPendingIdCard] = useState(null);

  // Issued-certificates viewer
  const [filterPayeeId, setFilterPayeeId] = useState(''); // '' = all payees
  const [filterMonth, setFilterMonth] = useState(moment().subtract(1, 'month').format('YYYY-MM'));
  const [certificates, setCertificates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Certificates are normally issued for last month's payments, so an empty
  // date field opens the calendar on the previous month.
  const prevMonth = useMemo(() => moment().subtract(1, 'month').startOf('month'), []);

  const selectedPayee = payees.find((p) => p.id === Number(selectedPayeeId));
  // Issue dropdown hides deactivated payees unless "show inactive" is on.
  const issuePayees = payees.filter((p) => p.active || showInactivePayees);

  const loadPayees = useCallback(async () => {
    try {
      const data = await WhtCertificateService.listPayees({ all: true });
      setPayees(data);
    } catch (e) {
      toast.error('โหลดรายชื่อผู้รับเงินไม่สำเร็จ');
    }
  }, []);

  useEffect(() => { loadPayees(); }, [loadPayees]);

  // ---- issue certificates ----

  const updateRow = (i, field, value) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);
  const removeRow = (i) => setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const rowTax = (amount) => (selectedPayee ? (Number(amount) || 0) * Number(selectedPayee.deductPercentage) / 100 : 0);

  const handleCreate = async () => {
    if (!selectedPayeeId) return toast.warn('เลือกผู้รับเงินก่อน');
    const items = rows
      .filter((r) => r.datePaid && r.datePaid.isValid() && r.amount !== '')
      .map((r) => ({ datePaid: r.datePaid.format('YYYY-MM-DD'), amount: Number(r.amount) }));
    if (items.length === 0) return toast.warn('กรอกวันที่และจำนวนเงินอย่างน้อย 1 รายการ');

    setCreating(true);
    try {
      const created = await WhtCertificateService.createCertificates(Number(selectedPayeeId), items);
      toast.success(`สร้างหนังสือรับรอง ${created.length} ใบสำเร็จ`);
      setRows([{ ...EMPTY_ROW }]);
      // Show them immediately in the viewer.
      setFilterPayeeId(String(selectedPayeeId));
      const first = created[0];
      if (first) setFilterMonth(moment(first.datePaid).format('YYYY-MM'));
    } catch (e) {
      toast.error(e.response?.data?.message || 'สร้างไม่สำเร็จ');
    } finally {
      setCreating(false);
    }
  };

  // Load the attached card for display. It comes back as a blob from the
  // authenticated route, so it becomes a local object URL that is revoked when the
  // dialog closes — the stored location never reaches the page.
  useEffect(() => {
    let revoked = false;
    let objectUrl = null;

    if (payeeDialogOpen && editingPayeeId && payeeForm.hasIdCard) {
      WhtCertificateService.fetchPayeeIdCardUrl(editingPayeeId)
        .then((url) => {
          if (revoked) { URL.revokeObjectURL(url); return; }
          objectUrl = url;
          setIdCardPreview(url);
        })
        .catch(() => setIdCardPreview(null));
    } else {
      setIdCardPreview(null);
    }

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [payeeDialogOpen, editingPayeeId, payeeForm.hasIdCard]);

  // The card is posted straight to the server, which stores it out of reach of
  // the payee API — only "is one on file" ever comes back.
  const handleUploadIdCard = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // let the same file be re-picked after a failure
    if (!file) return;

    // No payee to hang it off yet — hold it and preview locally until save.
    if (!editingPayeeId) {
      setPendingIdCard(file);
      setIdCardPreview(URL.createObjectURL(file));
      return;
    }

    setUploadingIdCard(true);
    try {
      await WhtCertificateService.uploadPayeeIdCard(editingPayeeId, file);
      setPayeeForm((prev) => ({ ...prev, hasIdCard: true }));
      toast.success('แนบสำเนาบัตรประชาชนแล้ว');
      // Re-fetch so the preview shows the file just uploaded, not the old one.
      try {
        const url = await WhtCertificateService.fetchPayeeIdCardUrl(editingPayeeId);
        setIdCardPreview(url);
      } catch (_) { /* preview is a convenience; the upload already succeeded */ }
      await loadPayees();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'แนบไฟล์ไม่สำเร็จ');
    } finally {
      setUploadingIdCard(false);
    }
  };

  // ---- payee add / edit ----

  const openAddPayee = () => {
    setEditingPayeeId(null);
    setPayeeForm({ ...EMPTY_PAYEE });
    setPendingIdCard(null);
    setPayeeDialogOpen(true);
  };

  const openEditPayee = () => {
    if (!selectedPayee) return;
    setEditingPayeeId(selectedPayee.id);
    setPayeeForm({
      name: selectedPayee.name || '',
      taxId: selectedPayee.taxId || '',
      address: selectedPayee.address || '',
      payeeType: selectedPayee.payeeType || 'company',
      deductPercentage: selectedPayee.deductPercentage,
      incomeDescription: selectedPayee.incomeDescription || '',
      incomeBox: selectedPayee.incomeBox || 6,
      numberOfCopy: selectedPayee.numberOfCopy,
      hasIdCard: !!selectedPayee.hasIdCard,
      active: selectedPayee.active,
    });
    setPayeeDialogOpen(true);
  };

  const handleSavePayee = async () => {
    if (!payeeForm.name) return toast.warn('กรอกชื่อผู้รับเงิน');
    setSavingPayee(true);
    try {
      if (editingPayeeId) {
        await WhtCertificateService.updatePayee(editingPayeeId, payeeForm);
        toast.success('แก้ไขผู้รับเงินสำเร็จ');
        setPayeeDialogOpen(false);
        await loadPayees();
      } else {
        const created = await WhtCertificateService.createPayee(payeeForm);
        toast.success('เพิ่มผู้รับเงินสำเร็จ');
        if (pendingIdCard && payeeForm.payeeType === 'individual') {
          try {
            await WhtCertificateService.uploadPayeeIdCard(created.id, pendingIdCard);
          } catch (err) {
            // The payee is already saved — say so, rather than implying nothing was.
            toast.error(`บันทึกผู้รับเงินแล้ว แต่แนบสำเนาบัตรไม่สำเร็จ — เปิดแก้ไขเพื่อแนบใหม่ (${err.response?.data?.message || err.message})`);
          }
        }
        setPendingIdCard(null);
        setPayeeDialogOpen(false);
        await loadPayees();
        setSelectedPayeeId(String(created.id));
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSavingPayee(false);
    }
  };

  // ---- viewer ----

  const loadCertificates = useCallback(async () => {
    if (!filterMonth) { setCertificates([]); return; }
    setLoadingList(true);
    try {
      const [year, month] = filterMonth.split('-');
      const data = await WhtCertificateService.listCertificates({ payeeId: filterPayeeId || undefined, year, month });
      setCertificates(data);
      setSelectedIds([]);
    } catch (e) {
      toast.error('โหลดรายการไม่สำเร็จ');
    } finally {
      setLoadingList(false);
    }
  }, [filterPayeeId, filterMonth]);

  useEffect(() => { loadCertificates(); }, [loadCertificates]);

  const allSelected = certificates.length > 0 && selectedIds.length === certificates.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : certificates.map((c) => c.id));
  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handlePrint = async () => {
    if (selectedIds.length === 0) return toast.warn('เลือกรายการที่จะพิมพ์ก่อน');
    setPrinting(true);
    try {
      // Payroll certificates print with their ใบสำคัญจ่าย; supplier ones print alone.
      await WhtCertificateService.openCertificatesPdf({ ids: selectedIds, withVouchers: true });
      await loadCertificates(); // refresh printed status
    } catch (e) {
      toast.error('พิมพ์ไม่สำเร็จ');
    } finally {
      setPrinting(false);
    }
  };

  const handleDelete = async (cert) => {
    if (!window.confirm(`ลบหนังสือรับรอง ${cert.certificateNo}?\nลบแล้วออกใหม่ได้ (เลขที่จะไม่ถูกนำกลับมาใช้)`)) return;
    try {
      await WhtCertificateService.deleteCertificate(cert.id);
      toast.success(`ลบ ${cert.certificateNo} แล้ว`);
      await loadCertificates();
    } catch (e) {
      toast.error(e.response?.data?.message || 'ลบไม่สำเร็จ');
    }
  };

  const totalTax = certificates.reduce((s, c) => s + Number(c.taxWithheld), 0);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        หนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ)
      </Typography>

      {/* ---- Issue ---- */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>ออกหนังสือรับรอง</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 320 }} size="small">
            <InputLabel>ผู้รับเงิน</InputLabel>
            <Select
              label="ผู้รับเงิน"
              value={selectedPayeeId}
              onChange={(e) => setSelectedPayeeId(e.target.value)}
            >
              {issuePayees.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.name} — {Number(p.deductPercentage)}% ({p.payeeType === 'company' ? 'นิติบุคคล' : 'บุคคลธรรมดา'})
                  {!p.active && ' (ปิดใช้งาน)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button startIcon={<PersonAddIcon />} onClick={openAddPayee}>
            เพิ่มผู้รับเงิน
          </Button>
          <Button startIcon={<EditIcon />} onClick={openEditPayee} disabled={!selectedPayee}>
            แก้ไขผู้รับเงิน
          </Button>
          <FormControlLabel
            control={<Checkbox size="small" checked={showInactivePayees} onChange={(e) => setShowInactivePayees(e.target.checked)} />}
            label="แสดงที่ปิดใช้งาน"
          />
          {selectedPayee && (
            <Chip label={`${selectedPayee.incomeDescription || 'อื่นๆ'} · ช่อง ${selectedPayee.incomeBox || 6} · หัก ${Number(selectedPayee.deductPercentage)}% · พิมพ์ ${selectedPayee.numberOfCopy} ชุด`} />
          )}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="35%">วันที่จ่าย</TableCell>
                <TableCell width="30%" align="right">จำนวนเงินที่จ่าย</TableCell>
                <TableCell width="25%" align="right">ภาษีหัก ณ ที่จ่าย</TableCell>
                <TableCell width="10%" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <DatePicker
                      value={row.datePaid}
                      onChange={(value) => updateRow(i, 'datePaid', value)}
                      format="DD/MM/YYYY"
                      referenceDate={prevMonth}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number" size="small" fullWidth
                      value={row.amount}
                      onChange={(e) => updateRow(i, 'amount', e.target.value)}
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">{baht(rowTax(row.amount))}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => removeRow(i)} disabled={rows.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button startIcon={<AddIcon />} onClick={addRow}>เพิ่มรายการ</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !selectedPayeeId}
          >
            {creating ? <CircularProgress size={22} /> : 'สร้างหนังสือรับรอง'}
          </Button>
        </Box>
      </Paper>

      {/* ---- View / print ---- */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>รายการที่ออกแล้ว / พิมพ์</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 320 }} size="small">
            <InputLabel>ผู้รับเงิน</InputLabel>
            <Select label="ผู้รับเงิน" value={filterPayeeId} onChange={(e) => setFilterPayeeId(e.target.value)}>
              <MenuItem value=""><em>ทั้งหมด</em></MenuItem>
              {payees.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatePicker
            label="เดือนที่จ่าย"
            views={['year', 'month']}
            openTo="month"
            format="MM/YYYY"
            value={filterMonth ? moment(filterMonth, 'YYYY-MM') : null}
            onChange={(value) => setFilterMonth(value && value.isValid() ? value.format('YYYY-MM') : '')}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="contained" startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={printing || selectedIds.length === 0}
          >
            {printing ? <CircularProgress size={22} /> : `พิมพ์ที่เลือก (${selectedIds.length})`}
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loadingList ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedIds.length > 0 && !allSelected}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>เลขที่</TableCell>
                  <TableCell>ผู้รับเงิน</TableCell>
                  <TableCell>วันที่จ่าย</TableCell>
                  <TableCell align="right">จำนวนเงิน</TableCell>
                  <TableCell align="right">ภาษีหัก</TableCell>
                  <TableCell align="center">สถานะ</TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {certificates.map((c) => (
                  <TableRow key={c.id} hover selected={selectedIds.includes(c.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} />
                    </TableCell>
                    <TableCell>{c.certificateNo}</TableCell>
                    <TableCell>{c.payee?.name || '-'}</TableCell>
                    <TableCell>{moment(c.datePaid).format('DD/MM/YYYY')}</TableCell>
                    <TableCell align="right">{baht(c.amountPaid)}</TableCell>
                    <TableCell align="right">{baht(c.taxWithheld)}</TableCell>
                    <TableCell align="center">
                      {c.printedAt
                        ? <Chip size="small" color="success" label="พิมพ์แล้ว" />
                        : <Chip size="small" variant="outlined" label="ยังไม่พิมพ์" />}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleDelete(c)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {certificates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                      ไม่มีรายการ เลือกเดือนที่ต้องการ
                    </TableCell>
                  </TableRow>
                )}
                {certificates.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>รวมภาษีหัก</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{baht(totalTax)}</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ---- Add payee dialog ---- */}
      <Dialog open={payeeDialogOpen} onClose={() => setPayeeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPayeeId ? 'แก้ไขผู้รับเงิน (WHT)' : 'เพิ่มผู้รับเงิน (WHT)'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="ชื่อผู้รับเงิน" required fullWidth
              value={payeeForm.name}
              onChange={(e) => setPayeeForm({ ...payeeForm, name: e.target.value })}
            />
            <TextField
              label="เลขประจำตัวผู้เสียภาษี" fullWidth
              value={payeeForm.taxId}
              onChange={(e) => setPayeeForm({ ...payeeForm, taxId: e.target.value })}
            />
            <TextField
              label="ที่อยู่" fullWidth multiline minRows={2}
              value={payeeForm.address}
              onChange={(e) => setPayeeForm({ ...payeeForm, address: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>ประเภท</InputLabel>
                <Select
                  label="ประเภท" value={payeeForm.payeeType}
                  onChange={(e) => setPayeeForm({ ...payeeForm, payeeType: e.target.value })}
                >
                  <MenuItem value="company">นิติบุคคล (ภ.ง.ด.53)</MenuItem>
                  <MenuItem value="individual">บุคคลธรรมดา (ภ.ง.ด.3)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="อัตราหัก %" type="number" fullWidth
                value={payeeForm.deductPercentage}
                onChange={(e) => setPayeeForm({ ...payeeForm, deductPercentage: e.target.value })}
                inputProps={{ min: 0, step: '0.5' }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="ประเภทเงินได้ (เช่น ค่าขนส่ง)" fullWidth
                value={payeeForm.incomeDescription}
                onChange={(e) => setPayeeForm({ ...payeeForm, incomeDescription: e.target.value })}
              />
              {/* Only affects certificates created from now on; each certificate
                  keeps the box it was issued with. */}
              <FormControl size="small" sx={{ width: 220, flexShrink: 0 }}>
                <InputLabel>ช่องใน 50 ทวิ</InputLabel>
                <Select
                  label="ช่องใน 50 ทวิ" value={payeeForm.incomeBox}
                  onChange={(e) => setPayeeForm({ ...payeeForm, incomeBox: e.target.value })}
                >
                  <MenuItem value={5}>5. ม.3 เตรส (ค่าเช่า ฯลฯ)</MenuItem>
                  <MenuItem value={6}>6. อื่นๆ</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="จำนวนชุดที่พิมพ์" type="number" sx={{ width: 160 }}
                value={payeeForm.numberOfCopy}
                onChange={(e) => setPayeeForm({ ...payeeForm, numberOfCopy: e.target.value })}
                inputProps={{ min: 1, step: 1 }}
              />
            </Box>
            {/* Printed on the lower half of the ใบสำคัญจ่าย. Only for บุคคลธรรมดา —
                a นิติบุคคล has no ID card to attach. On a new payee the file is
                held and uploaded right after the payee is created, since it must
                exist before a file can hang off it. The image itself is never
                returned by the API; the server only says whether one is on file. */}
            {payeeForm.payeeType === 'individual' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button variant="outlined" component="label" disabled={uploadingIdCard}>
                    {uploadingIdCard
                      ? <CircularProgress size={22} />
                      : ((payeeForm.hasIdCard || pendingIdCard) ? 'เปลี่ยนสำเนาบัตรประชาชน' : 'แนบสำเนาบัตรประชาชน')}
                    <input hidden type="file" accept="image/*" onChange={handleUploadIdCard} />
                  </Button>
                  <Box sx={{ fontSize: 13, color: (payeeForm.hasIdCard || pendingIdCard) ? 'success.main' : 'text.secondary' }}>
                    {payeeForm.hasIdCard
                      ? 'แนบแล้ว — ใช้ในใบสำคัญจ่าย'
                      : pendingIdCard
                        ? 'เลือกไฟล์แล้ว — จะแนบเมื่อกดบันทึก'
                        : 'ยังไม่ได้แนบ (ใบสำคัญจ่ายจะเว้นว่างไว้)'}
                  </Box>
                </Box>
                {/* Shown at the shape it prints on the voucher, so a bad crop is
                    obvious here rather than after the document is issued. */}
                {(payeeForm.hasIdCard || pendingIdCard) && (
                  <Box sx={{ mt: 1.5 }}>
                    {idCardPreview ? (
                      <Box
                        component="img"
                        src={idCardPreview}
                        alt="สำเนาบัตรประชาชน"
                        sx={{
                          width: 320, height: 202, objectFit: 'contain',
                          border: '1px solid', borderColor: 'divider',
                          borderRadius: 1, bgcolor: 'grey.50',
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: 320, height: 202, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', border: '1px dashed', borderColor: 'divider',
                        borderRadius: 1, color: 'text.secondary', fontSize: 13,
                      }}>
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
            {editingPayeeId && (
              <FormControlLabel
                control={<Switch checked={!!payeeForm.active} onChange={(e) => setPayeeForm({ ...payeeForm, active: e.target.checked })} />}
                label={payeeForm.active ? 'ใช้งาน' : 'ปิดใช้งาน (ซ่อนจากรายการออกหนังสือ)'}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayeeDialogOpen(false)}>ยกเลิก</Button>
          <Button variant="contained" onClick={handleSavePayee} disabled={savingPayee}>
            {savingPayee ? <CircularProgress size={22} /> : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </LocalizationProvider>
  );
};

export default WhtCertificatePage;
